import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useDragOrClick, useDragParentGroup } from "./useDragParentGroup";
import MaxVideoLimitHint from "../MaxVideoLimitHint";
import useMediaQuery from "../../hooks/useMediaQuery";

export default function SchemeSceneContent({
  parentGroupId,
  hasScreenshot,
  hasVideo = false,
  screenshotStatus,
  imageKey,
  imageSrc,
  hasImagePreview = false,
  imagePreviewSrc,
  buttonCount = 0,
  maxButtons = 2,
  isTrainer = false,
  contentMode = "video",
  contentText = "",
  textPanelTitle = "Текст",
  textPlaceholder = "Введите текст",
  onContentModeChange,
  onContentTextCommit,
  onVideoSelected,
  onImageSelected,
  onAddButton,
  buttonText,
  onButtonTextChange,
  availableMessengers = [],
  mediaUploadState = null,
  uploadProgress = 0,
}) {
  const [panel, setPanel] = useState(null);
  const [textDraft, setTextDraft] = useState(contentText);
  const [imgError, setImgError] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const prevPanelRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const startGroupDrag = useDragParentGroup(parentGroupId);
  const openVideoPanel = useDragOrClick(parentGroupId, () => setPanel("video"));
  const openButtonPanel = useDragOrClick(parentGroupId, () => setPanel("button"));
  const openTextPanel = useDragOrClick(parentGroupId, () => setPanel("text"));

  const canAddButton = buttonCount < maxButtons;
  const isTextMode = contentMode === "text";
  const isVideoMode = contentMode === "video";
  const isImageMode = contentMode === "image";
  const showVideoScreenshot = isVideoMode && hasScreenshot && imageSrc && !imgError;
  const showScreenshotPlaceholder = isVideoMode && hasVideo && !hasScreenshot;
  const hasVideoContent = hasVideo || hasScreenshot;
  const showImagePreview = isImageMode && hasImagePreview && imagePreviewSrc && !imgError;

  useEffect(() => {
    setImgError(false);
  }, [imageSrc, imagePreviewSrc, imageKey]);

  useEffect(() => {
    if (panel === "text" && prevPanelRef.current !== "text") {
      setTextDraft(contentText);
    }
    prevPanelRef.current = panel;
  }, [panel, contentText]);

  useEffect(() => {
    if (mediaUploadState !== "success") return undefined;
    const timer = window.setTimeout(() => setPanel(null), 1200);
    return () => window.clearTimeout(timer);
  }, [mediaUploadState]);

  const closePanel = () => {
    if (panel === "text") {
      onContentTextCommit?.(textDraft);
    }
    setPanel(null);
  };

  const handleVideoPick = (event) => {
    onVideoSelected?.(event);
  };

  const handleImagePick = (event) => {
    onImageSelected?.(event);
  };

  const handleAddButton = () => {
    onAddButton();
    closePanel();
  };

  const renderPanelBody = () => {
    if (panel === "button") {
      return (
        <div className="scheme-scene__overlay-body">
          <span className="scheme-scene__overlay-title">
            {isTrainer ? "Вариант ответа" : "Новая кнопка"}
          </span>
          <TextField
            placeholder={isTrainer ? "Текст ответа" : "Текст кнопки"}
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
            autoFocus
            value={buttonText}
            onChange={(event) => onButtonTextChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleAddButton();
            }}
          />
          <button
            type="button"
            className="scheme-scene__overlay-submit"
            onClick={handleAddButton}
          >
            Добавить
          </button>
        </div>
      );
    }

    if (panel === "text") {
      return (
        <div className="scheme-scene__overlay-body">
          <span className="scheme-scene__overlay-title">{textPanelTitle}</span>
          <TextField
            placeholder={textPlaceholder}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={isMobile ? 8 : 10}
            maxRows={isMobile ? 12 : 16}
            autoFocus
            value={textDraft}
            onChange={(event) => setTextDraft(event.target.value)}
          />
          <button
            type="button"
            className="scheme-scene__overlay-submit"
            onClick={closePanel}
          >
            Готово
          </button>
        </div>
      );
    }

    return (
      <div className="scheme-scene__overlay-body">
        <span className="scheme-scene__overlay-title">Загрузка видео</span>
        <p className="scheme-scene__video-limit">
          Размер видеофайла не должен превышать 100 МБ.
        </p>
        {mediaUploadState === "uploading" ? (
          <div className="scheme-scene__upload-progress-wrap">
            <p className="scheme-scene__upload-status scheme-scene__upload-status--loading">
              Загружаем видео… {uploadProgress > 0 ? `${uploadProgress}%` : ""}
            </p>
            <div className="scheme-scene__progress-bar" role="progressbar" aria-valuenow={uploadProgress} aria-valuemin="0" aria-valuemax="100">
              <div
                className="scheme-scene__progress-fill"
                style={{ width: `${Math.max(4, uploadProgress)}%` }}
              />
            </div>
          </div>
        ) : null}
        {mediaUploadState === "success" ? (
          <p className="scheme-scene__upload-status scheme-scene__upload-status--success">
            Видео загружено
          </p>
        ) : null}
        {mediaUploadState === "error" ? (
          <p className="scheme-scene__upload-status scheme-scene__upload-status--error">
            Не удалось загрузить видео
          </p>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          hidden
          onChange={handleVideoPick}
        />
        <button
          type="button"
          className="scheme-scene__overlay-submit scheme-scene__overlay-submit--video"
          autoFocus
          disabled={mediaUploadState === "uploading"}
          onClick={() => fileInputRef.current?.click()}
        >
          {hasVideoContent ? "Заменить видео" : "Выбрать видеофайл"}
        </button>
        <MaxVideoLimitHint availableMessengers={availableMessengers} />
      </div>
    );
  };

  const renderPanelOverlay = (mobile = false) => (
    <div
      className={`scheme-scene__overlay nodrag nopan scheme-scene__overlay--${panel}${
        mobile ? " scheme-scene__overlay--mobile" : ""
      }`}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="scheme-scene__overlay-close"
        onClick={closePanel}
        aria-label="Закрыть"
      >
        <CloseIcon fontSize="small" />
      </button>
      {renderPanelBody()}
    </div>
  );

  const contentDialog =
    (panel === "text" || panel === "video")
      ? createPortal(
          <div
            className="scheme-scene-dialog"
            role="dialog"
            aria-modal="true"
            onClick={closePanel}
            onKeyDown={(event) => {
              if (event.key === "Escape") closePanel();
            }}
          >
            <div
              className={`scheme-scene-dialog__panel scheme-scene-dialog__panel--${panel}`}
              onClick={(event) => event.stopPropagation()}
            >
              {renderPanelOverlay(false)}
            </div>
          </div>,
          document.body
        )
      : null;

  const mediaClassName = [
    "scheme-scene__media-wrap",
    "nodrag",
    "nopan",
    isTextMode
      ? "scheme-scene__media-wrap--text"
      : showVideoScreenshot || showImagePreview
        ? "scheme-scene__media-wrap--filled"
        : imgError
          ? "scheme-scene__media-wrap--error"
          : "scheme-scene__media-wrap--empty",
  ].join(" ");

  const handleCardPointerDown = (event) => {
    if (
      event.target.closest(
        ".scheme-scene__media-wrap, .scheme-scene__tool, .scheme-scene__overlay, .scheme-scene__mode"
      )
    ) {
      return;
    }
    startGroupDrag(event);
  };

  return (
    <div
      className={`scheme-scene__card${
        panel ? " scheme-scene__card--panel-open" : ""
      }`}
      onPointerDown={handleCardPointerDown}
    >
      <div
        className={mediaClassName}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div
          className="scheme-scene__mode scheme-scene__mode--overlay nodrag nopan"
          onPointerDown={(event) => event.stopPropagation()}
          hidden={Boolean(panel)}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={contentMode}
            onChange={(_, value) => {
              if (value) onContentModeChange?.(value);
            }}
          >
            <ToggleButton value="text" aria-label="Текст">
              <TextFieldsOutlinedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="video" aria-label="Видео">
              <VideocamOutlinedIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="image" aria-label="Картинка">
              <ImageOutlinedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        {isTextMode && contentText?.trim() ? (
          <div className="scheme-scene__text-preview nodrag nopan">
            {contentText}
          </div>
        ) : null}

        {showVideoScreenshot ? (
          <img
            key={imageKey}
            src={imageSrc}
            alt=""
            className="scheme-scene__thumb"
            onError={() => setImgError(true)}
          />
        ) : null}

        {showScreenshotPlaceholder ? (
          <div className="scheme-scene__thumb-placeholder nodrag nopan">
            {screenshotStatus === "failed"
              ? "Не удалось создать скриншот, но видео загружено"
              : "Скоро здесь появится скриншот из вашего видео"}
          </div>
        ) : null}

        {showImagePreview ? (
          <img
            key={`${imageKey}-content`}
            src={imagePreviewSrc}
            alt=""
            className="scheme-scene__thumb"
            onError={() => setImgError(true)}
          />
        ) : null}

        {panel === "button" ? renderPanelOverlay(false) : null}
      </div>

      {contentDialog}

      {!panel && (
        <div
          className={`scheme-scene__toolbar${
            !canAddButton ? " scheme-scene__toolbar--single" : ""
          }${isTrainer ? " scheme-scene__toolbar--trainer" : " scheme-scene__toolbar--stacked"}`}
        >
          {isTextMode ? (
            <button
              type="button"
              className="scheme-scene__tool"
              onPointerDown={openTextPanel}
            >
              <TextFieldsOutlinedIcon fontSize="inherit" />
              {isTrainer ? "Вопрос" : "Нажмите для ввода текста"}
            </button>
          ) : isImageMode ? (
            <>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImagePick}
              />
              <button
                type="button"
                className="scheme-scene__tool"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => imageInputRef.current?.click()}
              >
                <ImageOutlinedIcon fontSize="inherit" />
                {hasImagePreview ? "Заменить картинку" : "Загрузить картинку"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="scheme-scene__tool"
              onPointerDown={openVideoPanel}
            >
              <VideocamOutlinedIcon fontSize="inherit" />
              {hasVideoContent ? "Заменить видео" : "Загрузить видео"}
            </button>
          )}

          {canAddButton ? (
            <button
              type="button"
              className="scheme-scene__tool"
              onPointerDown={openButtonPanel}
            >
              <AddIcon fontSize="inherit" />
              {isTrainer ? "Ответ" : "Кнопка"}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
